// src/pages/InformeCliPro.jsx
import React, { useMemo, useState } from "react";
import {
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Spinner,
  Alert,
  Table,
} from "react-bootstrap";

import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

import { getInformeCliPro } from "../helpers/rutaReportes"; // 👈 helper nuevo

const fmtARS = (n) =>
  Number(n || 0).toLocaleString("es-AR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const todayYYYYMMDD = () => new Date().toISOString().slice(0, 10);
const sortES = (a, b) =>
  (a || "").localeCompare(b || "", "es", { sensitivity: "base" });

export default function InformeCliPro() {
  const [fecha, setFecha] = useState(todayYYYYMMDD());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const consultar = async () => {
    setErr("");
    setLoading(true);
    try {
      const r = await getInformeCliPro(fecha);
      if (!r?.ok) throw new Error(r?.error || r?.msg || "No se pudo cargar el reporte");
      setData(r);
    } catch (e) {
      setData(null);
      setErr(e?.message || "Error");
    } finally {
      setLoading(false);
    }
  };

  const exportarPDF = async () => {
  if (!data) return;

  // ===================== CONFIG “MARCA” =====================
  const LOGO_URL = "/Danlu.png"; // en /public
  const EMPRESA = "TRANSPORTE DANLU";
  const LINEA1 = "Dirección / Localidad / Provincia";
  const LINEA2 = "Tel: xxx  •  mail@dominio.com";
  const FOOTER_TXT = "Documento electrónico generado por el sistema DANLU";

  const fmtARS = (n) =>
    Number(n || 0).toLocaleString("es-AR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const safeFile = (s) =>
    String(s || "")
      .trim()
      .replace(/[^\w]+/g, "_")
      .replace(/^_+|_+$/g, "");

  // ===================== helper imagen =====================
  const loadImageAsDataURL = async (url) => {
    try {
      const r = await fetch(url, { cache: "no-store" });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const mime = blob.type || "";
      const dataUrl = await new Promise((resolve, reject) => {
        const fr = new FileReader();
        fr.onload = () => resolve(fr.result);
        fr.onerror = reject;
        fr.readAsDataURL(blob);
      });
      return { dataUrl, mime };
    } catch {
      return { dataUrl: null, mime: "" };
    }
  };

  const { dataUrl: logoDataUrl, mime: logoMime } = await loadImageAsDataURL(LOGO_URL);

  // ===================== PDF base =====================
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();

  const M_LEFT = 10;
  const M_RIGHT = 10;
  const M_TOP = 8;
  const M_BOTTOM = 8;

  const HEADER_H = 30;
  const FOOTER_H = 10;

  const title = "Resumen de Saldos (Clientes / Transportes)";
  const subtitle = `Fecha de corte: ${data?.fechaCorte || fecha}`;

  // ===================== header =====================
  const drawHeader = () => {
    const y0 = M_TOP + 2;

    if (logoDataUrl) {
      const LOGO_W = 18;
      const LOGO_H = 14;
      const format = (logoMime || "").includes("jpeg") ? "JPEG" : "PNG";
      try {
        doc.addImage(logoDataUrl, format, M_LEFT, y0, LOGO_W, LOGO_H);
      } catch {}
    }

    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text(EMPRESA, pageW / 2, y0 + 6, { align: "center" });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.text(LINEA1, pageW / 2, y0 + 11, { align: "center" });
    doc.text(LINEA2, pageW / 2, y0 + 15, { align: "center" });

    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text(title, M_LEFT, y0 + 22);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text(subtitle, M_LEFT, y0 + 27);

    doc.setDrawColor(180);
    doc.line(M_LEFT, M_TOP + HEADER_H, pageW - M_RIGHT, M_TOP + HEADER_H);
  };

  // ===================== footer =====================
  const drawFooter = (pageNumber, totalPages) => {
    const y = pageH - M_BOTTOM;

    doc.setDrawColor(200);
    doc.line(M_LEFT, y - FOOTER_H + 2, pageW - M_RIGHT, y - FOOTER_H + 2);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text(FOOTER_TXT, M_LEFT, y - 2);
    doc.text(`Página ${pageNumber} de ${totalPages}`, pageW - M_RIGHT, y - 2, {
      align: "right",
    });
  };

  // ===================== resumen totales =====================
  const tot = data?.totales || {};
  const drawTotales = (startY) => {
    let y = startY;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(25, 25, 25);

    doc.text(`Total Clientes: $ ${fmtARS(tot.clientes)}`, M_LEFT, y);
    y += 5;
    doc.text(`Total Transportes: $ ${fmtARS(tot.transportes)}`, M_LEFT, y);
    y += 5;
    doc.text(`Neto (Cli - Trp): $ ${fmtARS(tot.neto)}`, M_LEFT, y);
    y += 5;

    doc.setTextColor(220, 53, 69);

    doc.text(
      `Pactado bruto a terceros: $ ${fmtARS(tot.pactadoBruto)}`,
      M_LEFT, y
    );
    y += 5;
    doc.text(
      `Comisiones aplicadas: $ ${fmtARS(tot.comision)}`,
      M_LEFT, y
    );
    y += 5;
    doc.text(
      `Merma aplicada: $ ${fmtARS(tot.merma)}`,
      M_LEFT, y
    );
    y += 5;
    doc.text(
      `Pactado neto a terceros: $ ${fmtARS(tot.pactadoNeto)}`,
      M_LEFT, y
    );

    doc.setTextColor(25, 25, 25);
    return y + 4;
  };

  // ===================== render =====================
  drawHeader();
  let yCursor = drawTotales(M_TOP + HEADER_H + 8);

  // -------- tabla CLIENTES --------
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("Clientes", M_LEFT, yCursor + 4);

  autoTable(doc, {
    startY: yCursor + 7,
    head: [["Razón social", "Saldo"]],
    body: (clientes || []).map((x) => [
      x?.razonsocial || x?.nombre || "",
      `$ ${fmtARS(x?.saldo)}`,
    ]),
    styles: { fontSize: 8, cellPadding: 1.4 },
    headStyles: { fontStyle: "bold" },
    columnStyles: {
      1: { halign: "right", cellWidth: 45 },
    },
    margin: {
      left: M_LEFT,
      right: M_RIGHT,
      top: M_TOP + HEADER_H + 48,
      bottom: M_BOTTOM + FOOTER_H,
    },
    didDrawPage: () => {
      drawHeader();
      drawTotales(M_TOP + HEADER_H + 8);
    },
  });

  // -------- tabla TRANSPORTES --------
  let y2 = (doc.lastAutoTable?.finalY || yCursor + 20) + 10;

  // si no entra, nueva página
  if (y2 > pageH - (M_BOTTOM + FOOTER_H + 30)) {
    doc.addPage();
    drawHeader();
    y2 = drawTotales(M_TOP + HEADER_H + 8) + 8;
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("Transportes", M_LEFT, y2);

  autoTable(doc, {
    startY: y2 + 3,
    head: [["Transporte", "Comisiones", "Merma", "Saldo"]],

    body: (transportes || []).map((x) => [
      x?.transporte || x?.razonsocial || x?.nombre || "",
      `$ ${fmtARS(x?.comision)}`,
      `$ ${fmtARS(x?.merma)}`,
      `$ ${fmtARS(x?.saldo)}`,
    ]),

    styles: { fontSize: 8, cellPadding: 1.4 },
    headStyles: { fontStyle: "bold" },

    columnStyles: {
      0: { cellWidth: 70 },
      1: { halign: "right", cellWidth: 40, textColor: [220, 53, 69] },
      2: { halign: "right", cellWidth: 40, textColor: [220, 53, 69] },
      3: { halign: "right", cellWidth: 40 },
    },
    margin: {
      left: M_LEFT,
      right: M_RIGHT,
      top: M_TOP + HEADER_H + 48,
      bottom: M_BOTTOM + FOOTER_H,
    },
    didDrawPage: () => {
      drawHeader();
      drawTotales(M_TOP + HEADER_H + 8);
    },
  });

  // footers
  const totalPages = doc.getNumberOfPages();
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);
    drawFooter(p, totalPages);
  }

  doc.save(`informe-cli-trp-${safeFile(data?.fechaCorte || fecha)}.pdf`);
};

  const tot = data?.totales || {};

  const clientes = useMemo(() => {
    return (data?.clientes || [])
      .filter((x) => Number(x?.saldo || 0) !== 0)
      .sort((a, b) => sortES(a?.razonsocial, b?.razonsocial));
  }, [data]);

  const transportes = useMemo(() => {
    return (data?.transportes || [])
      .filter((x) => Number(x?.saldo || 0) !== 0)
      .sort((a, b) => sortES(a?.transporte, b?.transporte));
  }, [data]);

  return (
    <Container fluid="md" className="py-3">
      <Card className="shadow-sm border-0">
        <Card.Body>
          <div className="d-flex flex-column flex-md-row align-items-md-end justify-content-between gap-3">
            <div style={{ minWidth: 240 }}>
              <Form.Group>
                <Form.Label className="mb-1">Fecha de corte</Form.Label>
                <Form.Control
                  type="date"
                  value={fecha}
                  onChange={(e) => setFecha(e.target.value)}
                />
              </Form.Group>
            </div>

            <div className="d-flex gap-2">
              <Button onClick={consultar} disabled={loading}>
                {loading ? (
                  <>
                    <Spinner size="sm" /> Consultando…
                  </>
                ) : (
                  "Consultar"
                )}
              </Button>

              <Button
                variant="secondary"
                onClick={exportarPDF}
                disabled={!data || loading}
                >
                Exportar PDF
              </Button>
            </div>
          </div>

          {err ? (
            <Alert className="mt-3 mb-0" variant="danger">
              {err}
            </Alert>
          ) : null}
        </Card.Body>
      </Card>

      {!data ? (
        <div className="mt-3 text-muted">
          Elegí una fecha y tocá <b>“Consultar”</b>.
        </div>
      ) : (
        <>
          <Row className="mt-3 g-2">
            <Col md={4}>
              <Card className="border-0 shadow-sm">
                <Card.Body className="py-3">
                  <div className="text-muted" style={{ fontSize: 12 }}>Total Clientes</div>
                  <div className="fw-bold" style={{ fontSize: 24, lineHeight: 1.1 }}>
                    $ {fmtARS(tot.clientes)}
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col md={4}>
              <Card className="border-0 shadow-sm">
                <Card.Body className="py-3">
                  <div className="text-muted" style={{ fontSize: 12 }}>Total Transportes</div>
                  <div className="fw-bold" style={{ fontSize: 24, lineHeight: 1.1 }}>
                    $ {fmtARS(tot.transportes)}
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col md={4}>
              <Card className="border-0 shadow-sm">
                <Card.Body className="py-3">
                  <div className="text-muted" style={{ fontSize: 12 }}>Neto (Cli - Tra)</div>
                  <div className="fw-bold" style={{ fontSize: 24, lineHeight: 1.1 }}>
                    $ {fmtARS(tot.neto)}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row className="mt-2 g-2">
            {[
              { label: "Pactado bruto a terceros", valor: tot.pactadoBruto },
              { label: "Comisiones aplicadas", valor: tot.comision },
              { label: "Merma aplicada", valor: tot.merma },
              { label: "Pactado neto a terceros", valor: tot.pactadoNeto },
            ].map(({ label, valor }) => (
              <Col xs={12} md={6} lg={3} key={label}>
                <Card className="border-0 shadow-sm">
                  <Card.Body className="py-3 text-danger">
                    <div style={{ fontSize: 12 }}>{label}</div>
                    <div
                      className="fw-bold"
                      style={{ fontSize: 22, lineHeight: 1.1 }}
                    >
                      $ {fmtARS(valor)}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>

          <div className="text-muted mt-2" style={{ fontSize: 12 }}>
            Importes acumulados hasta la fecha de corte, incluidos los
            transportes con saldo cero. Los saldos ya incluyen los descuentos.
          </div>

          <Card className="mt-3 shadow-sm border-0">
            <Card.Body>
              <h5 className="mb-3">Clientes</h5>
              <div className="table-responsive">
                <Table striped bordered hover size="sm" className="mb-0" style={{ tableLayout: "fixed" }}>
                  <thead>
                    <tr>
                      <th>Razón social</th>
                      <th style={{ width: 160, textAlign: "right" }}>Saldo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clientes.length === 0 ? (
                      <tr><td colSpan={2} className="text-center">Sin saldos.</td></tr>
                    ) : (
                      clientes.map((c) => (
                        <tr key={c.id || c._id || c.razonsocial}>
                          <td>{c.razonsocial}</td>
                          <td style={{ textAlign: "right" }}>$ {fmtARS(c.saldo)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>

          <Card className="mt-3 shadow-sm border-0">
            <Card.Body>
              <h5 className="mb-3">Transportes</h5>
              <div className="table-responsive">
                <Table striped bordered hover size="sm" className="mb-0" style={{ tableLayout: "fixed" }}>
                  <thead>
                    <tr>
                      <th>Transporte</th>
                      <th className="text-danger text-end">Comisiones</th>
                      <th className="text-danger text-end">Merma</th>
                      <th className="text-end">Saldo</th>
                    </tr>
                  </thead>

                  <tbody>
                    {transportes.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="text-center">
                          Sin saldos.
                        </td>
                      </tr>
                    ) : (
                      transportes.map((t) => (
                        <tr key={t.id || t._id || t.transporte}>
                          <td>{t.transporte}</td>
                          <td className="text-danger text-end">
                            $ {fmtARS(t.comision)}
                          </td>
                          <td className="text-danger text-end">
                            $ {fmtARS(t.merma)}
                          </td>
                          <td className="text-end">
                            $ {fmtARS(t.saldo)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </>
      )}
    </Container>
  );
}